import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppGlobals } from '@app/app.global';
import { EntityService } from '@app/core/services';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';


export interface Supplier {
  id: string;
  name: string;
  alias?: string;
  supplierAlias?: string;
  supplier_alias?: string;
  gstNo?: string | string[];
  gst_no?: string | string[];
  mobile?: string;
  email?: string;
}

const EXTERNAL_SUPPLIER_FLAGS: Array<{ camel: string; snake: string }> = [
  { camel: 'isHyperpureEnabled', snake: 'is_hyperpure_enabled' },
  { camel: 'isUdaanEnabled', snake: 'is_udaan_enabled' },
  { camel: 'isDmartEnabled', snake: 'is_dmart_enabled' },
  { camel: 'isDeliveritEnabled', snake: 'is_deliverit_enabled' },
  { camel: 'isSwiggyAssureEnabled', snake: 'is_swiggy_assure_enabled' }
];

export function isIntegrationFlagEnabled(response: any, camelKey: string, snakeKey: string): boolean {
  return !!(response?.[camelKey] || response?.[snakeKey]);
}

/** True when Hyperpure, Udaan, DMart and DeliverIt are all disabled on the entity. */
export function areAllExternalSuppliersDisabled(response: any): boolean {
  if (!response) return false;
  return EXTERNAL_SUPPLIER_FLAGS.every(flag => !isIntegrationFlagEnabled(response, flag.camel, flag.snake));
}

/**
 * Hyperpure compare/search rates are exclusive of tax; other platforms already send tax-inclusive rates.
 * Applies GST + CESS from taxDetails and rounds to 2 decimals.
 */
export function getHyperpureTaxInclusiveRate(exclusiveRate?: number | null, taxDetails?: any[] | null): number {
  const rate = Number(exclusiveRate) || 0;
  if (!rate) {
    return 0;
  }
  if (!Array.isArray(taxDetails) || taxDetails.length === 0) {
    return Math.round(rate * 100) / 100;
  }
  const totalTaxPercent = taxDetails.reduce((sum, tax) => {
    return sum + Number(tax?.taxRate ?? tax?.tax_rate ?? 0);
  }, 0);
  return Math.round(rate * (1 + totalTaxPercent / 100) * 100) / 100;
}

/** Applies tax-inclusive Hyperpure rates to a list of raw search/purchase items. */
export function applyHyperpureInclusiveTaxToItems<T extends { rate?: number; taxDetails?: any[]; tax_details?: any[] }>(items: T[]): T[] {
  if (!Array.isArray(items)) {
    return [];
  }
  return items.map(item => {
    const taxDetails = item?.taxDetails || item?.tax_details;
    return {
      ...item,
      rate: getHyperpureTaxInclusiveRate(item?.rate, taxDetails)
    };
  });
}

/** Default items searched for users with no external supplier integrations. */
export const DEMO_DEFAULT_SEARCH_ITEMS = ['Milk', 'Sugar', 'Paneer'] as const;

const PM_CONNECT_FORM_EMBED_KEY = 'e51ae1e7737cc2a72b655bddaba37606f2073f17358030e39b6174ce4fa7a4f7';
const PM_CONNECT_FORM_BASE_URL = 'https://marketplaceadminnewapi.petpooja.com/public/forms';
const PM_CONNECT_LEAD_LAMBDA_URL = 'https://5ul6nqewpq3onpsulwuolaswri0pnuae.lambda-url.ap-south-1.on.aws';

export interface PmConnectLeadPayload {
  full_name: string;
  phone_number: string;
  email: string;
  city: string;
  business_name: string;
  captchaToken?: string;
}

export interface PmConnectFormCityOption {
  value: string;
  label: string;
}

@Injectable({
  providedIn: 'root'
})
export class PriceDiscoveryService {
  // DMart API base URL - using direct DMart API (proxy removed)
  // OTP methods now use new aidemods.petpooja.site endpoints
  // Other methods below still reference old DMart endpoints and may need updates
  private readonly DMART_API_BASE_URL = 'https://digital.dmart.in/api/';
  private readonly connectLeadHttp: HttpClient;

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
    private entityService: EntityService,
    private appGlobals: AppGlobals
  ) {
    this.connectLeadHttp = new HttpClient(httpBackend);
  }

  private isObjectId(value: string): boolean {
    return /^[0-9a-fA-F]{24}$/.test(value || '');
  }

  private extractEntityId(entityId: any): string {
    if (typeof entityId === 'string') {
      return entityId.trim();
    }
    if (entityId?.id) {
      return String(entityId.id).trim();
    }
    if (entityId?._id) {
      return String(entityId._id).trim();
    }
    return '';
  }

  /**
   * Prefer a valid ObjectId so x-entity-ids and companyId stay in sync
   * with the new dashboard entity checks.
   */
  private resolveAuthorizedEntityId(entityId: any): string {
    const requested = this.extractEntityId(entityId);
    if (this.isObjectId(requested)) {
      return requested;
    }

    const currentEntity = this.entityService.getSingleSelectedEntity();
    const fallback = this.extractEntityId(currentEntity);
    return this.isObjectId(fallback) ? fallback : '';
  }

  private getHeaders(entityId: any): HttpHeaders {
    const authorizedId = this.resolveAuthorizedEntityId(entityId);
    const id = authorizedId || this.extractEntityId(entityId);

    return new HttpHeaders({
      'x-entity-ids': id || ''
    });
  }

  getEntities(): Observable<any[]> {
    // Get entities from the core EntityService (localStorage)
    const entities = this.entityService.getEntities();
    return of(entities || []);
  }

  getSuppliers(entityId: string, dropdown: boolean = true): Observable<Supplier[]> {
    const headers = this.getHeaders(entityId);
    const params: any = {};
    if (dropdown) params.dropdown = 'true';
    return this.http.get<Supplier[]>(`${this.appGlobals.apiUrl.dashboard}supplier-list`, {
      headers,
      params
    });
  }

  getComparisonData(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}supplier-list`, { headers });
  }

  getRecentPurchaseItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}last-20-purchase-items`, params, { headers });
  }

  getMostPurchasedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}most-purchased-items`, params, { headers });
  }

  getMostSearchedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}most-searched-items`, params, { headers });
  }

  getLastSearchedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}last-searched-items`, params, { headers });
  }

  getRecentSearchedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any[]>(`${this.appGlobals.apiUrl.dashboard}pm/fast-tags/recent-searched-items`, { headers, params });
  }

  getTrendingNearYouItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any[]>(`${this.appGlobals.apiUrl.dashboard}pm/fast-tags/trending-near-you-items`, { headers, params });
  }

  getTopOrderedItems(params: any, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}top-ordered-items`, params, { headers });
  }

  /**
   * Get HP (Hyperpure) recent purchases for the entity.
   * Returns live_items (available) and missing_items (price not found).
   * GET /pm/fast-tags/hp-recent-purchases?days=60
   */
  getHpRecentPurchases(params: { days: number }, entityId: string): Observable<any> {
    const authorizedId = this.resolveAuthorizedEntityId(entityId);
    const headers = this.getHeaders(authorizedId || entityId);
    const queryParams: { [key: string]: string } = { days: params.days.toString() };
    if (authorizedId) {
      queryParams.companyId = authorizedId;
    }
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}pm/fast-tags/hp-recent-purchases`, {
      headers,
      params: queryParams
    });
  }

  searchProducts(searchTerm: string, entityId: string): Observable<any[]> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any[]>(`${this.appGlobals.apiUrl.dashboard}search-products`, { searchTerm }, { headers });
  }

  searchHyperpure(searchTerm: string, entityId: string, storeUuid: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}search`, {
      headers,
      params: {
        search_term: searchTerm,
        store_uuid: storeUuid
      }
    });
  }

  /**
   * Search the linked Hyperpure catalogue by item name.
   * Unit matching happens on the results; the Hyperpure search API takes the name.
   */
  searchHyperpureItems(itemName: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}search`, {
      headers,
      params: {
        item_name: itemName
      }
    });
  }

  addToCart(productId: number, quantity: number, deliveryMode: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}cart`, {
      product_id: productId,
      quantity,
      delivery_mode: deliveryMode
    }, { headers });
  }

  addMultipleItemsToCart(items: any[], entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}cart`, {
      items: items
    }, { headers });
  }

  getCart(storeUuid: string, deliveryMode: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}cart`, {
      headers,
      params: {
        store_uuid: storeUuid,
        delivery_mode: deliveryMode,
        get_details: 'true'
      }
    });
  }

  checkHyperpureEnabled(entityId: string): Observable<any> {
    // Use the comprehensive integration status endpoint from authentication service
    // This returns all integrations (Hyperpure, Udaan, DMart) in one call
    return this.checkIntegrationStatus(entityId);
  }

  /**
   * Check integration status for all suppliers
   * Returns enabled status for Hyperpure, Dmart, Udaan, etc.
   */
  checkIntegrationStatus(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}check-integration-status/${entityId}`, {
      headers,
      params: { t: Date.now().toString() }
    });
  }

  /**
   * Get purchase manager configuration including supplier preferences for Auto-Fill Smart Cart.
   * purchasemanagerConfig is now included in the check-integration-status response — no separate call needed.
   */
  getPurchaseManagerConfig(entityId: string): Observable<any> {
    return this.checkIntegrationStatus(entityId);
  }

  /**
   * Update purchase manager configuration.
   * Sends all suppliers in drag-sort order with autoFill flags.
   * supplierPreferrence: Array<{ name: string; autoFill: boolean }>
   *   - Array order = drag-sort order (price discovery column order)
   *   - autoFill: true  → participates in auto-fill cart
   *   - autoFill: false → excluded from auto-fill but order is preserved
   */
  updatePurchaseManagerConfig(entityId: string, supplierPreferrence: { name: string; autoFill: boolean }[]): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}update-supplier-preferences`, {
      entityId,
      purchasemanagerConfig: { supplierPreferrence }
    }, { headers });
  }

  searchSuppliers(query: string, suppliers: Supplier[]): Supplier[] {
    return suppliers.filter(supplier =>
      supplier.name.toLowerCase().includes(query.toLowerCase())
    );
  }

  getHyperpureLoginData(entityId: string, payload: any, authToken?: string): Observable<any> {
    let headerObj: { [key: string]: string } = {
      'x-entity-ids': entityId
    };
    // When an explicit auth token is provided (e.g., mobile webview where
    // the TokenInterceptor doesn't have a token in localStorage), set the
    // Authorization header manually so the backend can authenticate the request.
    if (authToken) {
      const bearerToken = authToken.startsWith('Bearer ') || authToken.startsWith('bearer ')
        ? authToken
        : `Bearer ${authToken}`;
      headerObj['Authorization'] = bearerToken;
    }
    const headers = new HttpHeaders(headerObj);

    console.log('🔄 [HYPERPURE] getHyperpureLoginData Request Payload:', JSON.stringify(payload));
    console.log('🔄 [HYPERPURE] getHyperpureLoginData Request Headers:', {
      'x-entity-ids': headerObj['x-entity-ids'],
      'Authorization': headerObj['Authorization'] ? 'Bearer ...' : 'Missing'
    });

    // Calls backend /auth/sign-in which expects store_uuid, store_details, and user_details
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}auth/sign-in`, payload, { headers });
  }

  saveHyperpureStoreRegistration(response: any): Observable<any> {
    // Use the first entity ID from the response or current entity
    const entityId = this.entityService.getEntities()[0];
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}hyperpure/store-registration`, response, { headers });
  }

  // Purchase Manager - Compare items across suppliers
  comparePurchaseItems(params: {
    item_name: string;
    unit: string;
    quantity: number;
    location: string;
    hyperpure_store_uuid: string;
    hyperpure_session_id: string;
    [key: string]: any;
  }, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}purchase-manager`, params, { headers });
  }

  // Check Price Discovery Status - Poll for results
  checkPriceDiscoveryStatus(requestId: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}purchase-manager/status`, {
      headers,
      params: { request_id: requestId }
    });
  }

  // DMart Integration APIs - Using legacy endpoints

  /**
   * Send OTP for DMart login
   */
  sendDmartOtp(userId: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    const payload = {
      userId: userId,
      otpLogin: 'true'
    };

    return this.http.post<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/otp`,
      payload,
      { headers: headers }
    );
  }

  /**
   * Verify OTP and login to DMart
   */
  verifyDmartOtp(userid: string, otp: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    const payload = {
      userid: userid,
      otp: otp,
      entityId: entityId
    };

    return this.http.post<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/login`,
      payload,
      { headers: headers }
    );
  }

  /**
   * Get DMart cart summary from backend
   * Backend proxies the DMart cart API and returns cart items
   * @param storeId - DMart store ID
   * @param entityId - Entity ID
   */
  getDmartCart(storeId: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}dmart/cart-summary`, {
      headers,
      params: { storeId }
    });
  }

  /**
   * Add item to DMart cart via backend
   * Note: storeId is retrieved from the backend (saved during login)
   */
  addToDmartCart(productId: string, quantity: number, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    const payload = {
      orderItem: [{
        productId: productId,
        quantity: String(quantity)
      }]
    };

    return this.http.post<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/add-to-cart`,
      payload,
      { headers: headers }
    );
  }

  /**
   * Get DMart integration status
   * Check if DMart integration is active for the entity
   */
  getDmartIntegrationStatus(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    return this.http.get<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/integration-status`,
      { headers: headers }
    );
  }

  /**
   * Deactivate DMart integration
   * Disable DMart integration for the entity
   */
  deactivateDmartIntegration(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    return this.http.put<any>(
      `${this.appGlobals.apiUrl.dashboard}dmart/deactivate-integration`,
      {}, // Empty body as per API spec
      { headers: headers }
    );
  }

  /**
   * Get pincode suggestions from DMart
   * Calls DMart API directly (no backend logging needed for suggestions)
   */
  getDmartPincodeSuggestions(searchTerm: string, entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json'
    });

    // Dmart API expects searchText (not searchTerm)
    const dmartPayload = { searchText: searchTerm };

    // Call DMart API directly
    return this.http.post<any>(`${this.DMART_API_BASE_URL}v2/pincodes/suggestions`, dmartPayload, {
      headers: dmartHeaders
    });
  }

  /**
   * Get pincode details from DMart
   * Calls DMart API directly and logs to backend
   * @param uniqueId - Unique identifier from pincode suggestion
   * @param apiMode - API mode (e.g., 'pincode')
   * @param pincode - Optional pincode
   * @param currentLat - Optional latitude
   * @param currentLng - Optional longitude
   * @param entityId - Entity ID
   */
  getDmartPincodeDetails(uniqueId: string, apiMode: string, pincode: string = '', currentLat: string = '', currentLng: string = '', entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json'
    });

    // Dmart API expects full payload with all fields
    const dmartPayload = {
      uniqueId,
      apiMode,
      pincode,
      currentLat,
      currentLng
    };

    const backendPayload = {
      uniqueId,
      apiMode,
      pincode,
      currentLat,
      currentLng,
      entityId
    };

    // Call DMart API directly
    const dmartCall = this.http.post<any>(`${this.DMART_API_BASE_URL}v2/pincodes/details`, dmartPayload, {
      headers: dmartHeaders
    });

    // Send to backend to update DmartStore with pincode/region/store_id (important for DB sync)
    const backendHeaders = this.getHeaders(entityId);
    const backendCall = this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}dmart/pincode-details`, backendPayload, {
      headers: backendHeaders
    }).pipe(
      tap(() => console.log('✅ DMart store details updated in backend')),
      catchError(err => {
        console.warn('⚠️ Failed to update store details in backend:', err.message);
        return of(null);
      })
    );

    return forkJoin([dmartCall, backendCall]).pipe(
      map(([dmartResponse, _]) => dmartResponse)
    );
  }

  /**
   * Get earliest delivery slot from DMart
   * Calls DMart API directly (no backend logging needed for slot lookup)
   */
  getDmartEarliestSlot(pincode: string, storeId: string, entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*'
    });

    // Call DMart API directly
    return this.http.get<any>(`${this.DMART_API_BASE_URL}v2/pincodes/earliestslot/${pincode}?storeId=${storeId}`, {
      headers: dmartHeaders
    });
  }

  /**
   * Get cart summary from DMart
   * Calls DMart API directly with stored token (no backend logging)
   */
  getDmartCartSummary(dmToken: string, storeId: string, entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*',
      'Content-Type': 'application/json',
      'dm_token': dmToken,
      'storeid': storeId
    });

    // Call DMart API directly
    return this.http.get<any>(`${this.DMART_API_BASE_URL}v5/secure/cart?profile=summary&channel=web`, {
      headers: dmartHeaders
    });
  }

  /**
   * Search products in DMart
   * Calls DMart API directly (no backend logging needed for search)
   */
  searchDmartProducts(searchTerm: string, storeId: string, entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'accept': '*/*',
      'storeId': storeId
    });

    // Call DMart API directly
    return this.http.get<any>(`${this.DMART_API_BASE_URL}v3/search/${encodeURIComponent(searchTerm)}?storeId=${storeId}`, {
      headers: dmartHeaders
    });
  }

  /**
   * Add item to DMart cart
   * Uses Angular proxy -> ngrok -> DMart API
   */
  addDmartItemToCart(dmToken: string, storeId: string, orderItems: any[], entityId: string): Observable<any> {
    const dmartHeaders = new HttpHeaders({
      'Accept': '*/*',
      'Content-Type': 'application/json',
      'dm_token': dmToken,
      'storeid': storeId
    });

    const payload = { orderItem: orderItems };

    // Call via Angular proxy (proxies to ngrok)
    const url = `${this.DMART_API_BASE_URL}v1/secure/cart?profile=summary`;

    console.log('🛒 [DMart Cart API] Adding item via proxy->ngrok:');
    console.log('  Proxy URL:', url);
    console.log('  Items:', JSON.stringify(orderItems));

    return this.http.post<any>(url, payload, {
      headers: dmartHeaders
    }).pipe(
      tap(response => {
        console.log('✅ [DMart Cart API] Success:', response);
      }),
      catchError(error => {
        console.error('❌ [DMart Cart API] Error:', error);
        throw error;
      })
    );
  }

  // Udaan Cart APIs
  // Uses backend proxy: Authentication service -> Udaan API
  // Backend handles credentials from AWS Secrets Manager

  /**
   * Get Udaan cart items
   * GET /udaan/cart?store_id=<store_id>
   */
  getUdaanCart(storeId: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);

    const url = `${this.appGlobals.apiUrl.authentication}udaan/cart?store_id=${storeId}`;

    console.log('🛒 [Udaan GET Cart API] Fetching cart via backend:');
    console.log('  URL:', url);
    console.log('  Store ID:', storeId);

    return this.http.get<any>(url, { headers }).pipe(
      tap(response => {
        console.log('✅ [Udaan GET Cart API] Success:', response);
      }),
      catchError(error => {
        console.error('❌ [Udaan GET Cart API] Error:', error);
        throw error;
      })
    );
  }

  /**
   * Add/Update item in Udaan cart
   * POST /udaan/cart/update
   */
  updateUdaanCart(
    productId: string,
    qty: number,
    deliveryMode: string,
    storeUuid: string,
    categoryId: string | undefined, // Made optional
    entityId: string,
    vertical: string,
    skipRedirect: boolean = false // Added skipRedirect flag
  ): Observable<any> {
    const headers = this.getHeaders(entityId);

    const url = `${this.appGlobals.apiUrl.authentication}udaan/cart/update`;
    const body = {
      product_id: productId,
      qty: qty,
      delivery_mode: deliveryMode,
      store_uuid: storeUuid,
      category_id: categoryId,
      vertical: vertical
    };

    console.log('🛒 [Udaan Cart Update API] Adding/updating item via backend:');
    console.log('  URL:', url);
    console.log('  Body:', JSON.stringify(body));

    return this.http.post<any>(url, body, { headers }).pipe(
      tap(response => {
        console.log('✅ [Udaan Cart Update API] Success:', response);
        // Open Udaan website in new tab on successful cart update if NOT bulk mode
        if (!skipRedirect) {
          window.open('https://udaan.com/boltCart/?utm_source=petpooja', '_blank');
        }
      }),
      catchError(error => {
        console.error('❌ [Udaan Cart Update API] Error:', error);
        throw error;
      })
    );
  }

  // Favorite Items APIs
  getFavoriteItems(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}favorite-items`, { headers });
  }

  addFavoriteItem(entityId: string, itemName: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}favorite-items`, {
      item_name: itemName
    }, { headers });
  }

  removeFavoriteItem(entityId: string, itemName: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.delete<any>(`${this.appGlobals.apiUrl.dashboard}favorite-items`, {
      headers,
      body: { item_name: itemName }
    });
  }

  updateRateCardItem(data: any, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.put<any>(`${this.appGlobals.apiUrl.dashboard}update-rate-card-item`, data, { headers });
  }

  // Add Supplier with Rate Card - Upload supplier and rate contract file
  addSupplierWithRateCard(formData: FormData, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}add-supplier-rate-card-with-file`, formData, { headers });
  }

  // Update Supplier Rate Card - Upload rate contract file for existing supplier
  updateSupplierWithRateCard(formData: FormData, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}update-supplier-rate-card-file`, formData, { headers });
  }

  // Get supplier rate card items
  getSupplierRateCardItems(params: any, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}supplier-rate-card-items`, {
      headers,
      params
    });
  }

  deleteRateCardItem(rateCardItemId: string, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.request('delete', `${this.appGlobals.apiUrl.dashboard}delete-rate-card-item`, {
      headers,
      body: { rateCardItemId }
    });
  }

  addSupplierRateCardItems(payload: any, entityId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}add-supplier-rate-card-items`, payload, { headers });
  }

  // ─────────────────────────────────────────────────────────────
  // PM Favourite Products APIs
  // ─────────────────────────────────────────────────────────────

  /**
   * Get all favourite products for a company
   * GET /pm/favourites?companyId=XXX
   */
  getFavouriteProducts(companyId: string): Observable<any> {
    const entityId = this.resolveAuthorizedEntityId(companyId);
    const headers = this.getHeaders(entityId || companyId);
    const params: { [key: string]: string } = {};
    if (entityId) {
      params.companyId = entityId;
    }
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}pm/favourites`, {
      headers,
      params
    });
  }

  /**
   * Add a product to favourites
   * POST /pm/favourites
   */
  addFavouriteProduct(payload: {
    companyId: string;
    supplierName: string;
    supplierDisplayName?: string;
    productName: string;
    productSkuId: string;
    supplierId?: string;
    dmartArticleNumber?: string;
  }): Observable<any> {
    const entityId = this.resolveAuthorizedEntityId(payload.companyId);
    const headers = this.getHeaders(entityId || payload.companyId);
    const body: any = {
      supplierName: payload.supplierName,
      supplierDisplayName: payload.supplierDisplayName,
      productName: payload.productName,
      productSkuId: payload.productSkuId
    };

    if (entityId) {
      body.companyId = entityId;
    }
    if (this.isObjectId(payload.supplierId || '')) {
      body.supplierId = payload.supplierId;
    }
    if (payload.dmartArticleNumber) {
      body.dmartArticleNumber = payload.dmartArticleNumber;
    }

    return this.http.post<any>(`${this.appGlobals.apiUrl.dashboard}pm/favourites`, body, { headers });
  }

  /**
   * Remove a product from favourites
   * DELETE /pm/favourites
   */
  removeFavouriteProduct(payload: {
    companyId: string;
    supplierName: string;
    productSkuId: string;
    dmartArticleNumber?: string;
  }): Observable<any> {
    const entityId = this.resolveAuthorizedEntityId(payload.companyId);
    const headers = this.getHeaders(entityId || payload.companyId);
    const body: any = {
      supplierName: payload.supplierName,
      productSkuId: payload.productSkuId
    };

    if (entityId) {
      body.companyId = entityId;
    }
    if (payload.dmartArticleNumber) {
      body.dmartArticleNumber = payload.dmartArticleNumber;
    }

    return this.http.request<any>('delete', `${this.appGlobals.apiUrl.dashboard}pm/favourites`, {
      headers,
      body
    });
  }

  /**
   * Search all favourite items across suppliers (Hyperpure, Udaan, Dmart, Local).
   * Backend fetches fav items for the entity, groups by supplier, calls each supplier's
   * search API, and returns consolidated product results.
   *
   * GET /pm/favourites/search
   * Headers: x-entity-ids
   * Query: companyId
   * Response: { hyperpure: { best_match, similar_items }, udaan: {...}, dmart: {...}, supplierWithRateCard: {...} }
   */
  searchFavouriteItems(entityId: string): Observable<any> {
    const authorizedId = this.resolveAuthorizedEntityId(entityId);
    const headers = this.getHeaders(authorizedId || entityId);
    const params: { [key: string]: string } = {};
    if (authorizedId) {
      params.companyId = authorizedId;
    }
    return this.http.get<any>(`${this.appGlobals.apiUrl.dashboard}pm/favourites/search`, {
      headers,
      params
    });
  }

  getDeliverItCart(storeId: string, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    const url = `${this.appGlobals.apiUrl.authentication}deliverit/cart`;
    return this.http.get<any>(url, { headers }).pipe(
      tap(response => {
        console.log('✅ [DeliverIt GET Cart API] Success:', response);
      }),
      catchError(error => {
        console.error('❌ [DeliverIt GET Cart API] Error:', error);
        throw error;
      })
    );
  }

  getSwiggyAssureCart(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}swiggy-assure/cart`, { headers });
  }

  getSwiggyAssureCartSession(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}swiggy-assure/cart/session`, { headers });
  }

  refreshSwiggyAssureCart(entityId: string, purchaseStoreId?: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}swiggy-assure/cart/refresh`, {
      purchaseStoreId: purchaseStoreId || ''
    }, { headers });
  }

  updateSwiggyAssureCart(item: { itemId: string; productId: string; spin: string; quantity: number; purchaseStoreId?: string }, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}swiggy-assure/cart/update`, item, { headers });
  }

  clearSwiggyAssureCart(entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.post<any>(`${this.appGlobals.apiUrl.authentication}swiggy-assure/cart/clear`, {}, { headers });
  }

  updateDeliverItCart(productId: string, qty: number, entityId: string): Observable<any> {
    const headers = this.getHeaders(entityId);
    const url = `${this.appGlobals.apiUrl.authentication}deliverit/cart/update`;
    const body = {
      productUniqueId: productId,
      qty: qty
    };
    return this.http.post<any>(url, body, { headers }).pipe(
      tap(response => {
        console.log('✅ [DeliverIt Cart Update API] Success:', response);
      }),
      catchError(error => {
        console.error('❌ [DeliverIt Cart Update API] Error:', error);
        throw error;
      })
    );
  }

  deliveritSignIn(entityId: string, payload: any): Observable<any> {
    const headers = new HttpHeaders({
      'x-entity-ids': entityId
    });
    const url = `${this.appGlobals.apiUrl.authentication}deliverit/auth/sign-in`;
    return this.http.post<any>(url, payload, { headers }).pipe(
      tap(response => {
        console.log('✅ [DeliverIt Sign-In API] Success:', response);
      }),
      catchError(error => {
        console.error('❌ [DeliverIt Sign-In API] Error:', error);
        throw error;
      })
    );
  }

  getPendingInventoryPurchaseRequests(entityId: string, page: number = 1, limit: number = 10): Observable<any> {
    const headers = this.getHeaders(entityId);
    return this.http.get<any>(`${this.appGlobals.apiUrl.authentication}pending-inventory-purchase-requests`, {
      headers,
      params: {
        entityId,
        page: page.toString(),
        limit: limit.toString()
      }
    });
  }

  updateInventoryPurchaseRequestStatus(
    requestId: string,
    processStatus?: string,
    entityId?: string,
    itemIds?: string[],
    itemProcessStatus?: string
  ): Observable<any> {
    const headers = this.getHeaders(entityId);
    const body: any = { requestId };
    if (processStatus) body.processStatus = processStatus;
    if (itemIds && itemIds.length > 0) body.itemIds = itemIds;
    if (itemProcessStatus) body.itemProcessStatus = itemProcessStatus;

    return this.http.put<any>(`${this.appGlobals.apiUrl.authentication}inventory-purchase-request/status`, body, { headers });
  }

  getPmConnectFormCities(): Observable<PmConnectFormCityOption[]> {
    return this.connectLeadHttp
      .get<any>(`${PM_CONNECT_FORM_BASE_URL}/${PM_CONNECT_FORM_EMBED_KEY}`)
      .pipe(
        map((response: any) => {
          const cityField = (response?.data?.fields || []).find((field: any) => field?.name === 'city');
          return (cityField?.options || [])
            .filter((option: any) => option && !option.archived && option.inForms !== false)
            .map((option: any) => ({
              value: String(option.value || ''),
              label: String(option.label || option.value || '')
            }))
            .filter((option: PmConnectFormCityOption) => !!option.value);
        }),
        catchError(error => {
          console.error('Failed to load connect form cities', error);
          return of([]);
        })
      );
  }

  submitPmConnectLead(payload: PmConnectLeadPayload): Observable<any> {
    // Only this Lambda URL bypasses interceptors (no auth/entity headers → no CORS preflight).
    return this.connectLeadHttp.post<any>(PM_CONNECT_LEAD_LAMBDA_URL, payload);
  }

  getDemoSupplierCarts(): Observable<any[]> {
    return this.http.get<any[]>('/assets/demo/purchase-manager/supplier-carts.json').pipe(
      catchError(() => of([]))
    );
  }
}

